import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useForm from '@afosto/utils-react/validation/useForm';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import { rbac as rbacApi } from '@afosto/iam/api/Rbac';
import TextField from '@afosto/app-components-react/TextField';
import Form from '@afosto/app-components-react/Form';
import Autocomplete from '@afosto/app-components-react/Autocomplete';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import validationSchema from './validations';
import translations from './translations';

const UserForm = ({ user, inline, onSubmit, onCancel }) => {
  const intl = useIntl();
  const [isFetchingRoles, setIsFetchingRoles] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const { first_name: firstName = '', last_name: lastName = '', email = '', roles = [] } =
    user || {};
  const currentUserRoles = roles.reduce((acc, role) => {
    const availableRole = findObjectInArray(availableRoles, 'value', role);
    return availableRole ? [...acc, availableRole] : acc;
  }, []);

  const form = useForm({
    validationSchema,
    editInline: inline,
    defaultValues: {
      firstName,
      lastName,
      email,
      roles: currentUserRoles,
    },
    onCancel,
    onSubmit,
  });
  const { setIsEditing } = form;

  useEffect(() => {
    let isMounted = true;

    const fetchAvailableRoles = async () => {
      setIsFetchingRoles(true);

      try {
        const [tenantRolesResponse, systemRolesResponse] = await Promise.all([
          rbacApi.listRoles(),
          rbacApi.listSystemRoles(),
        ]);

        const { data: tenantRolesData = [] } = tenantRolesResponse;
        const { data: systemRolesData = [] } = systemRolesResponse;
        const formattedAvailableRoles = [...systemRolesData, ...tenantRolesData].reduce(
          (acc, availableRole) => [
            ...acc,
            {
              label: availableRole.name,
              value: availableRole.id,
            },
          ],
          [],
        );

        if (isMounted) {
          setAvailableRoles(formattedAvailableRoles);
        }
      } catch (err) {
        if (isMounted) {
          setAvailableRoles([]);
        }
      } finally {
        setIsFetchingRoles(false);
      }
    };

    fetchAvailableRoles();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (inline) {
      setIsEditing(false);
    }
  }, [user, setIsEditing, inline]);

  return (
    <LoadingWrapper isLoading={form.isSubmitting}>
      <Form form={form}>
        <TextField
          name="firstName"
          label={intl.formatMessage(translations.fields.firstName.label)}
          readOnly={form.editInline}
          autoFocus
        />
        <TextField
          name="lastName"
          label={intl.formatMessage(translations.fields.lastName.label)}
          readOnly={form.editInline}
        />
        <TextField
          name="email"
          label={intl.formatMessage(translations.fields.email.label)}
          inline={!!user}
          readOnly={form.editInline || !!user}
        />

        <Autocomplete
          name="roles"
          label={intl.formatMessage(translations.fields.roles.label)}
          options={availableRoles}
          loading={isFetchingRoles}
          readOnly={form.editInline}
          multiple
        />
      </Form>
    </LoadingWrapper>
  );
};

UserForm.propTypes = {
  user: PropTypes.object,
  inline: PropTypes.bool,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
};

UserForm.defaultProps = {
  user: null,
  inline: false,
  onSubmit: undefined,
  onCancel: undefined,
};

export default UserForm;
